import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GeneralLayout from '../../../components/GeneralLayout';
import ContentBox from '../../../components/ContentBox';
import { getAllAchievements, awardAchievement, checkUserRoles } from '../../../services/api';
import { getBadgeUrl } from '../../../utils/badgeUrl';


function AchievementRow({ ach, idx, showGive, onGive }) {
    return (
        <div
            className={`w-full border-b border-black ${idx % 2 === 0 ? 'bg-black/5' : 'bg-black/10'}`}
        >
            <div className="flex gap-3 w-full">
                <div className="relative w-[80px] bg-offwhite flex items-center justify-center border-r border-black/20 px-2 py-2 group">
                    <img
                        src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                        alt={ach.badgeID}
                        className="shrink-0"
                        style={{ imageRendering: 'pixelated', maxWidth: 'none', maxHeight: 'none' }}
                        loading="lazy"
                    />
                    <div className="hidden group-hover:block absolute left-[50px] top-0 ml-2 z-50">
                        <div className="bg-offwhite border border-black p-2">
                            <div style={{ width: 'calc(var(--bw) * 3)', height: 'calc(var(--bh) * 3)' }}>
                                <img
                                    src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                                    alt={`${ach.badgeID} preview`}
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        img.parentElement.style.setProperty('--bw', `${img.naturalWidth || img.width}px`);
                                        img.parentElement.style.setProperty('--bh', `${img.naturalHeight || img.height}px`);
                                    }}
                                    style={{ imageRendering: 'pixelated', transform: 'scale(3)', transformOrigin: 'top left', maxWidth: 'none', maxHeight: 'none' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="min-w-0 flex-1 py-2 pr-2">
                    <div className="flex items-start justify-between gap-2">
                        <div className="text-coal font-['Jersey_10'] text-xl leading-none truncate" title={ach.name}>{ach.name}</div>
                        {showGive && (
                            <button
                                type="button"
                                onClick={() => onGive(ach)}
                                className="shrink-0 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 bg-maroon text-white hover:bg-red-900 transition-colors rounded"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                            >
                                Give
                            </button>
                        )}
                    </div>
                    <div className="mt-1 text-coal/90 text-xs truncate" title={ach.description}>{ach.description}</div>
                    {ach.earnedCount != null && (
                        <div className="mt-1 text-[11px] text-coal/60">
                            Earned by {ach.earnedCount.toLocaleString()} user{ach.earnedCount !== 1 ? 's' : ''} ({ach.earnedPct}%)
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function GiveModal({ ach, onConfirm, onCancel, submitting }) {
    const [habboName, setHabboName] = useState('');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
            <div className="relative bg-offwhite border border-black shadow-xl rounded p-5 w-full max-w-sm mx-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white border border-black/20 p-2 flex items-center justify-center">
                        <div style={{ width: 'calc(var(--bw2) * 3)', height: 'calc(var(--bh2) * 3)' }}>
                            <img
                                src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                                alt={ach.badgeID}
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    img.parentElement.style.setProperty('--bw2', `${img.naturalWidth || img.width}px`);
                                    img.parentElement.style.setProperty('--bh2', `${img.naturalHeight || img.height}px`);
                                }}
                                style={{ imageRendering: 'pixelated', transform: 'scale(3)', transformOrigin: 'top left', maxWidth: 'none', maxHeight: 'none' }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="font-['Jersey_10'] text-xl leading-none">{ach.name}</div>
                        <div className="text-xs text-coal/70 mt-0.5">{ach.description}</div>
                    </div>
                </div>
                <label className="block font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-700 mb-1">
                    Habbo Name
                </label>
                <input
                    type="text"
                    value={habboName}
                    onChange={(e) => setHabboName(e.target.value)}
                    placeholder="Enter Habbo username"
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm mb-4"
                    disabled={submitting}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter' && habboName.trim()) onConfirm(habboName.trim()); }}
                />
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => onConfirm(habboName.trim())}
                        disabled={submitting || !habboName.trim()}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {submitting ? 'Awarding...' : 'Confirm'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function AwardAchievements({ userData }) {
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [myRoles, setMyRoles] = useState(null);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [modalAchievement, setModalAchievement] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const isAdmin = userData?.hasAdminRole === true;

    useEffect(() => {
        if (!userData?.hasStaffRole) return;
        getAllAchievements()
            .then(data => {
                if (data?.success) setAchievements(data.achievements || []);
            })
            .catch(() => toast.error('Failed to load achievements'))
            .finally(() => setLoading(false));
    }, [userData?.hasStaffRole]);

    useEffect(() => {
        if (!userData?.habboName) {
            setRolesLoading(false);
            return;
        }
        checkUserRoles(userData.habboName)
            .then(data => {
                if (data?.success) setMyRoles({ isSTF: data.isStaff, isOCC: data.isOCC });
            })
            .catch(() => {})
            .finally(() => setRolesLoading(false));
    }, [userData?.habboName]);

    if (!userData || !userData.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    const isOCC = !isAdmin && myRoles?.isOCC === true;
    const isSTF = !isAdmin && myRoles?.isSTF === true;

    const staffBadges = achievements.filter(a => a.category === 'staff');
    const creatorBadges = achievements.filter(a => a.category === 'creator');

    const openGiveModal = (ach) => {
        setModalAchievement(ach);
    };

    const handleConfirm = async (habboName) => {
        if (!habboName) return;

        if (!isAdmin && habboName.toLowerCase() === (userData?.habboName || '').toLowerCase()) {
            toast.error('You cannot award achievements to yourself');
            return;
        }

        setSubmitting(true);
        try {
            const data = await awardAchievement(habboName, modalAchievement.badgeID);
            if (data?.success && data?.awarded) {
                toast.success(`Achievement awarded to ${habboName}!`);
                setModalAchievement(null);
            } else if (data?.success && !data?.awarded) {
                toast.warn(data.message || `${habboName} already has this achievement`);
            } else {
                toast.error(data?.message || 'Failed to award achievement');
            }
        } catch {
            toast.error('Failed to award achievement');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <GeneralLayout userData={userData}>
            <style>{`.habbo-scroll::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>

            <ContentBox theme="default" tint="lightskyblue" header="All Achievements" padding="p-0">
                {loading ? (
                    <div className="p-3 text-coal/70 text-sm">Loading achievements...</div>
                ) : achievements.length === 0 ? (
                    <div className="p-3 text-coal/70 text-sm">No achievements found.</div>
                ) : (
                    <div className="habbo-scroll overflow-y-auto flex flex-col" style={{ maxHeight: 300 }}>
                        {achievements.map((ach, idx) => (
                            <AchievementRow
                                key={ach.badgeID}
                                ach={ach}
                                idx={idx}
                                showGive={isAdmin}
                                onGive={openGiveModal}
                            />
                        ))}
                    </div>
                )}
            </ContentBox>

            {/* Staff Awards — STF only */}
            {(isAdmin || isSTF) && (
                <div className="mt-4">
                    <ContentBox theme="default" tint="lightskyblue" header="Staff Awards" padding="p-0">
                        <div className="flex flex-col">
                            <div className="p-2 border border-black/30 m-2">
                                Staff Note: Do not award achievements/badges for fun. Only award achievements you are permitted to award and do not give them to friends or other team members if they should not have them. <br/><span className="text-sm text-black/50">Please provide the badge, achievement name and achievement description to Steve if you would like to add a new achievement. </span>
                            </div>
                            {loading ? (
                                <div className="p-3 text-coal/70 text-sm">Loading...</div>
                            ) : staffBadges.length === 0 ? (
                                <div className="p-3 text-coal/70 text-sm">No staff awards configured. Assign achievements to the Staff team via Housekeeping → Manage Achievements.</div>
                            ) : (
                                <div className="habbo-scroll overflow-y-auto flex flex-col" style={{ maxHeight: 300 }}>
                                    {staffBadges.map((ach, idx) => (
                                        <AchievementRow key={ach.badgeID} ach={ach} idx={idx} showGive={true} onGive={openGiveModal} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </ContentBox>
                </div>
            )}

            {/* Creator Awards — OCC only */}
            {(isAdmin || isOCC) && (
                <div className="mt-4">
                    <ContentBox theme="default" tint="lightskyblue" header="Creator Awards" padding="p-0">
                        <div className="flex flex-col">
                            <div className="p-2 border border-black/30 m-2">
                                Creator Note: Only award these badges to users you are permitted to award them to. Do not give them to friends or other team members who should not have them. <br/><span className="text-sm text-black/50">Please provide the badge, achievement name and achievement description to Steve if you would like to add a new achievement. </span>
                            </div>
                            {loading ? (
                                <div className="p-3 text-coal/70 text-sm">Loading...</div>
                            ) : creatorBadges.length === 0 ? (
                                <div className="p-3 text-coal/70 text-sm">No creator awards configured. Assign achievements to the Creator team via Housekeeping → Manage Achievements.</div>
                            ) : (
                                <div className="habbo-scroll overflow-y-auto flex flex-col" style={{ maxHeight: 300 }}>
                                    {creatorBadges.map((ach, idx) => (
                                        <AchievementRow key={ach.badgeID} ach={ach} idx={idx} showGive={true} onGive={openGiveModal} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </ContentBox>
                </div>
            )}

            {modalAchievement && (
                <GiveModal
                    ach={modalAchievement}
                    onConfirm={handleConfirm}
                    onCancel={() => setModalAchievement(null)}
                    submitting={submitting}
                />
            )}
        </GeneralLayout>
    );
}

export default AwardAchievements;
